"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, ShieldCheck, Flame, TriangleAlert as AlertTriangle, PhoneCall, CircleCheck as CheckCircle2, Clock, CircleQuestionMark as HelpCircle, Zap, Gauge, Thermometer, FileCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
const navienModels = [{
  id: "npe-series",
  series: "NPE-2 Series (Condensing)",
  badge: "Top Efficiency 0.96 UEF",
  desc: "Premium condensing tankless water heaters including NPE-A2 (with ComfortFlow recirculation pump & buffer tank) and NPE-S2 standard condensing units.",
  features: ["Dual stainless steel heat exchangers", "Built-in recirculation pump (A2)", "Cascading capability up to 32 units", "0.96 UEF Ultra-Efficiency"],
  idealFor: "Medium to large homes demanding endless simultaneous hot water."
}, {
  id: "npn-series",
  series: "NPN Series (Non-Condensing)",
  badge: "Compact & Cost-Effective",
  desc: "Reliable non-condensing tankless units engineered for easy retrofitting into traditional standard venting configurations (NPN-U and NPN-E models).",
  features: ["Patented stainless steel burner", "Negative pressure gas valve", "Compact space-saving wall mount", "Indoor and outdoor options"],
  idealFor: "Homes upgrading from tank systems with existing 1/2-inch gas lines."
}, {
  id: "ncb-series",
  series: "NCB-H Series (Combi-Boilers)",
  badge: "Whole-Home Heat + Hot Water",
  desc: "High-efficiency condensing combination boilers delivering both domestic hot water and whole-house hydronic central heating from one single unit.",
  features: ["Dual heat exchangers", "Up to 15:1 turndown ratio", "Simultaneous space & water heating", "Compact single-appliance footprint"],
  idealFor: "Properties with radiant floor or hydronic radiator heating setups."
}];
const errorCodeData = [{
  code: "E003",
  meaning: "Ignition Failure",
  cause: "Interrupted gas supply, closed shutoff valve, or dirty spark igniter.",
  action: "Check gas valve. If gas is on, call our technicians to clean electrodes and calibrate gas pressure."
}, {
  code: "E012",
  meaning: "Flame Loss During Operation",
  cause: "Low gas supply pressure, flue gas recirculation, or contaminated flame rod.",
  action: "Inspect exhaust termination for blockages. Schedule a safety diagnostic check immediately."
}, {
  code: "E110",
  meaning: "Exhaust Flue Gas Overheat",
  cause: "Severe mineral scale inside heat exchanger or blocked venting pipe.",
  action: "Turn off unit. Requires professional descaling/flushing to prevent heat exchanger crack."
}, {
  code: "E016 / E046",
  meaning: "Heat Exchanger Overheat",
  cause: "Restricted water flow, clogged inlet water filter, or heavy calcium buildup.",
  action: "Clean cold water inlet filter. Call us for a comprehensive chemical flush and descaling service."
}, {
  code: "E738",
  meaning: "Outdoor Temperature Sensor Error",
  cause: "Damaged wire harness or faulty thermistor sensor probe.",
  action: "Electrical diagnostic required to test resistance and replace the sensor module."
}];
const troubleshootingFaqs = [{
  question: "Why does my Navien tankless water heater produce lukewarm water or fluctuate temperatures?",
  answer: "Temperature fluctuations ('cold water sandwich') typically occur when water flow falls below the minimum activation threshold (0.5 GPM), when cold water cross-contamination happens in single-handle fixtures, or when mineral scale restricts water flow through the secondary heat exchanger. We inspect flow sensors, flush scale, and calibrate temperature settings."
}, {
  question: "How often does a Navien tankless water heater need maintenance and descaling?",
  answer: "In Jonesboro and the surrounding metro area, municipal water hardness requires descaling every 12 to 18 months. Regular flushing removes calcium and magnesium deposits, preserves the 15-year factory warranty, and maintains peak 0.96 UEF efficiency."
}, {
  question: "What does the Navien factory warranty cover?",
  answer: "Most Navien residential tankless models feature a 15-year warranty on dual stainless steel heat exchangers, 5 years on general internal parts, and 1 year on labor when installed by a licensed plumber according to manufacturer specifications. We provide complete warranty diagnostic reports and direct parts replacement."
}, {
  question: "Can you convert my existing tank water heater to a Navien tankless system?",
  answer: "Yes. Navien NPE and NPN units support standard 1/2-inch gas lines up to 24 feet and use versatile PVC venting. We handle all gas line sizing, dedicated electrical wiring, venting modifications, and municipal permitting across Jonesboro, GA."
}];
const serviceCoveragePoints = [{
  title: "24/7 Urgent Repair",
  text: "Rapid response across Jonesboro for error codes, leaks, and sudden cold showers."
}, {
  title: "Factory-Trained Technicians",
  text: "Equipped with OEM Navien sensors, control boards, valves, and igniters."
}, {
  title: "Annual Descaling & Tune-Ups",
  text: "Complete chemical flushing, inlet filter cleaning, and combustion analysis."
}, {
  title: "Certified Replacements",
  text: "Full turnkey replacement with proper permits, gas testing, and warranty registration."
}];
export default function NavienServices() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("models");
  return <section id="navienwaterheaterservices" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-semibold text-xs tracking-wide uppercase">
            Specialized Brand Services
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            Navien Water Heater Repair, Maintenance & Installation
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Jonesboro Emergency Plumber provides factory-trained diagnostics, OEM parts replacement, and annual maintenance for all Navien tankless units, combi-boilers, and hybrid systems.
          </p>
        </div>

        {/* Quick Emergency Notice */}
        <Alert className="mb-12 border-accent/40 bg-accent/10 text-foreground">
          <AlertTriangle className="size-5 text-accent" />
          <AlertTitle className="font-semibold text-foreground">Have a flashing Navien Error Code or Complete Loss of Hot Water?</AlertTitle>
          <AlertDescription className="text-muted-foreground flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-1">
            <span>Our 24/7 Jonesboro technicians carry Navien diagnostic scanners, igniters, and sensors on every truck.</span>
            <Button asChild size="sm" className="w-fit">
              <a href="tel:4708479076" className="inline-flex items-center gap-1.5">
                <PhoneCall className="size-3.5" />
                Call (470) 847-9076
              </a>
            </Button>
          </AlertDescription>
        </Alert>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-8">
          <div className="flex justify-center">
            <TabsList className="grid grid-cols-2 md:grid-cols-4 w-full max-w-3xl h-auto p-1 bg-muted/80 rounded-xl">
              <TabsTrigger value="models" className="py-2.5 text-xs sm:text-sm font-medium">
                <Flame className="size-4 mr-1.5 inline-block" />
                Models We Service
              </TabsTrigger>
              <TabsTrigger value="diagnostics" className="py-2.5 text-xs sm:text-sm font-medium">
                <Gauge className="size-4 mr-1.5 inline-block" />
                Error Code Guide
              </TabsTrigger>
              <TabsTrigger value="warranty" className="py-2.5 text-xs sm:text-sm font-medium">
                <ShieldCheck className="size-4 mr-1.5 inline-block" />
                Warranty & Care
              </TabsTrigger>
              <TabsTrigger value="faq" className="py-2.5 text-xs sm:text-sm font-medium">
                <HelpCircle className="size-4 mr-1.5 inline-block" />
                Troubleshooting
              </TabsTrigger>
            </TabsList>
          </div>

          {/* TAB 1: MODELS */}
          <TabsContent value="models" className="mt-6 focus-visible:outline-none">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {navienModels.map((item, idx) => <motion.div key={item.id} data-index={idx} initial={shouldReduceMotion ? {} : {
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.35,
              delay: idx * 0.1
            }} className="h-full">
                  <Card className="h-full flex flex-col justify-between border-border hover:border-primary/40 shadow-sm transition-[border-color,box-shadow] duration-200">
                    <CardHeader className="pb-4">
                      <div className="flex justify-between items-start gap-2 mb-2">
                        <Badge variant="outline" className="text-xs bg-muted/50 border-border">
                          {item.badge}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-bold text-foreground">
                        {item.series}
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                        {item.desc}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-0 flex-1 flex flex-col justify-between">
                      <div className="space-y-2 border-t border-border pt-4">
                        <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Key Specifications:</span>
                        <ul className="space-y-1.5">
                          {item.features.map((feat, fIdx) => <li key={fIdx} data-index={fIdx} className="text-xs text-muted-foreground flex items-center gap-2">
                              <CheckCircle2 className="size-3.5 text-accent shrink-0" />
                              <span>{feat}</span>
                            </li>)}
                        </ul>
                      </div>
                      <div className="bg-muted/40 p-3 rounded-lg border border-border/60 mt-4 text-xs text-foreground">
                        <span className="font-semibold block mb-0.5">Application:</span>
                        <span className="text-muted-foreground">{item.idealFor}</span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>)}
            </div>
          </TabsContent>

          {/* TAB 2: ERROR CODES & DIAGNOSTICS */}
          <TabsContent value="diagnostics" className="mt-6 focus-visible:outline-none">
            <Card className="border-border shadow-sm overflow-hidden">
              <CardHeader className="border-b border-border bg-card">
                <CardTitle className="text-xl font-bold flex items-center gap-2">
                  <Wrench className="size-5 text-primary" />
                  Navien Error Codes & Diagnostic Chart
                </CardTitle>
                <CardDescription>
                  Reference guide for common front-panel alphanumeric error indicators on NPE, NPN, and NCB units.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader className="bg-muted/50">
                    <TableRow>
                      <TableHead className="font-semibold text-foreground w-[120px]">Code</TableHead>
                      <TableHead className="font-semibold text-foreground">Status / Meaning</TableHead>
                      <TableHead className="font-semibold text-foreground hidden sm:table-cell">Probable Root Cause</TableHead>
                      <TableHead className="font-semibold text-foreground">Recommended Resolution</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {errorCodeData.map((row, index) => <TableRow key={row.code} data-index={index}>
                        <TableCell className="font-mono font-bold text-destructive">{row.code}</TableCell>
                        <TableCell className="font-medium text-foreground">{row.meaning}</TableCell>
                        <TableCell className="text-muted-foreground text-xs hidden sm:table-cell">{row.cause}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">{row.action}</TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 3: WARRANTY & MAINTENANCE */}
          <TabsContent value="warranty" className="mt-6 focus-visible:outline-none">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-border shadow-sm">
                <CardHeader>
                  <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
                    <ShieldCheck className="size-5" />
                  </div>
                  <CardTitle className="text-xl font-bold">Navien Factory Warranty Standards</CardTitle>
                  <CardDescription>
                    Protecting your multi-year manufacturer coverage with certified maintenance.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
                  <p>
                    Navien provides an industry-leading 15-year warranty on stainless steel heat exchangers and 5 years on internal electronic/mechanical parts.
                  </p>
                  <div className="space-y-2 border-l-2 border-accent pl-3 my-2 text-foreground">
                    <p className="font-medium text-xs">Crucial Manufacturer Requirement:</p>
                    <p className="text-xs text-muted-foreground">
                      Navien warranties explicitly exclude damage caused by sediment, calcium scale accumulation, or uncertified installation without isolation valves.
                    </p>
                  </div>
                  <ul className="space-y-2 pt-2">
                    <li className="flex items-center gap-2 text-xs text-foreground">
                      <FileCheck className="size-4 text-primary shrink-0" />
                      We provide documented annual service records for warranty claims.
                    </li>
                    <li className="flex items-center gap-2 text-xs text-foreground">
                      <Zap className="size-4 text-primary shrink-0" />
                      Direct access to genuine OEM replacement components.
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-border shadow-sm">
                <CardHeader>
                  <div className="size-10 rounded-lg bg-accent/20 flex items-center justify-center text-foreground mb-2">
                    <Thermometer className="size-5" />
                  </div>
                  <CardTitle className="text-xl font-bold">Comprehensive Navien Maintenance Flush</CardTitle>
                  <CardDescription>
                    Keep 0.96 UEF efficiency and eliminate scale buildup inside Jonesboro homes.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <p>Our comprehensive 60-minute Navien preventative service covers:</p>
                  <ul className="space-y-2.5">
                    <li className="flex items-start gap-2 text-xs text-foreground">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                      <span><strong>Recirculating Descale:</strong> Food-grade virgin descaler circulated for 45 minutes to dissolve calcium crust without scoring stainless steel.</span>
                    </li>
                    <li className="flex items-start gap-2 text-xs text-foreground">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                      <span><strong>Cold Inlet Screen Sanitization:</strong> Removing debris, grit, and pipe scale from the primary inlet mesh.</span>
                    </li>
                    <li className="flex items-start gap-2 text-xs text-foreground">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                      <span><strong>Flame Rod & Combustion Check:</strong> Testing manifold pressure, venting clearance, and igniter spark gaps.</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TAB 4: TROUBLESHOOTING ACCORDION */}
          <TabsContent value="faq" className="mt-6 focus-visible:outline-none">
            <Card className="border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl font-bold">Navien Brand Troubleshooting Guide</CardTitle>
                <CardDescription>
                  Answers to frequent questions and symptoms specific to Navien tankless and combination systems.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {troubleshootingFaqs.map((faq, fIdx) => <AccordionItem key={fIdx} value={`item-${fIdx}`} data-index={fIdx}>
                      <AccordionTrigger className="text-left font-semibold text-foreground text-sm hover:no-underline hover:text-primary">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>)}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Local Coverage Bar */}
        <div className="mt-14 rounded-2xl bg-secondary/60 border border-border p-6 sm:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {serviceCoveragePoints.map((pt, pIdx) => <div key={pIdx} data-index={pIdx} className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Clock className="size-4 text-primary shrink-0" />
                  <h3 className="font-semibold text-foreground text-sm">{pt.title}</h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{pt.text}</p>
              </div>)}
          </div>
        </div>

        {/* CTA Footer Block */}
        <div className="mt-12 text-center flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button asChild size="lg" className="w-full sm:w-auto font-semibold">
            <Link to="/tankless-water-heater-service/">
              Book Tankless Service
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
            <Link to="/contact/">
              Request Free Estimate
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}